import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Avatar, Dropdown, Button, theme } from "antd";
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  DollarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  SettingOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  AlertOutlined,
  FileTextOutlined,
  LineChartOutlined,
  AppstoreOutlined,
  DesktopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  CustomerServiceOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  BarChartOutlined,
  ExperimentOutlined,
  BankOutlined,
  PieChartOutlined,
  ApiOutlined,
  WalletOutlined,
  AuditOutlined,
  HistoryOutlined,
  TransactionOutlined,
  ControlOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  BugOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";

import Logo from "@assets/logo_powow@2x.png";
import LogoIcon from "@assets/logocompany.png";

const { Header, Sider, Content } = Layout;

// Menu configuration
// requiredPermissions: user needs at least ONE of these action_codes to see the item
// requiredRoleTypes: user needs one of these role types to see the item
// Superadmin bypasses all permission checks
const menuItems = [
  {
    key: "dashboard",
    icon: <DashboardOutlined />,
    label: "Dashboard",
    path: "/dashboard",
    requiredPermissions: ["dashboard:read"],
  },
  {
    key: "groupManagement",
    icon: <TeamOutlined />,
    label: "Group Management",
    children: [
      {
        key: "groupSummary",
        icon: <AppstoreOutlined />,
        label: "Group Summary",
        path: "/group-management/groups",
        requiredPermissions: ["viewgroup:read", "dispatchgroup:read"],
      },
      {
        key: "customerApprovals",
        icon: <CheckCircleOutlined />,
        label: "Customer Approvals",
        path: "/customers/bindings",
        requiredPermissions: ["binding:read"],
      },
      {
        key: "onboard",
        icon: <PlusCircleOutlined />,
        label: "System Onboard",
        path: "/group-management/onboard",
        requiredPermissions: ["system:onboard"],
      },
      {
        key: "offboard",
        icon: <MinusCircleOutlined />,
        label: "System Offboard",
        path: "/group-management/offboard",
        requiredPermissions: ["system:offboard"],
      },
      {
        key: "nmiChange",
        icon: <EditOutlined />,
        label: "NMI / SN Change",
        path: "/group-management/nmi-change",
        requiredPermissions: ["approval:create", "approval:review"],
      },
    ],
  },
  {
    key: "systems",
    icon: <DesktopOutlined />,
    label: "Systems",
    children: [
      {
        key: "systemsSummary",
        icon: <UnorderedListOutlined />,
        label: "Systems Summary",
        path: "/systems/summary",
        requiredPermissions: ["system:read", "system:protected_view"],
      },
      {
        key: "systemsSearch",
        icon: <SearchOutlined />,
        label: "Search System",
        path: "/systems/search",
        requiredPermissions: ["system:read", "system:protected_view"],
      },
    ],
  },
  {
    key: "vppControl",
    icon: <ControlOutlined />,
    label: "VPP Control",
    children: [
      {
        key: "manageControlGroup",
        icon: <AppstoreOutlined />,
        label: "Manage Control Group",
        path: "/control-group/summary",
        requiredPermissions: ["dispatchgroup:read"],
      },
      {
        key: "biddingCentre",
        icon: <ThunderboltOutlined />,
        label: "Bidding Centre",
        children: [
          {
            key: "biddingSummary",
            icon: <BarChartOutlined />,
            label: "Bidding Summary",
            path: "/bidding/summary",
            requiredPermissions: ["dispatch:read"],
          },
          {
            key: "biddingRules",
            icon: <SettingOutlined />,
            label: "Bidding Rules",
            path: "/bidding/rules",
            requiredPermissions: ["dispatch:read"],
          },
        ],
      },
      {
        key: "dispatchCentre",
        icon: <ThunderboltOutlined />,
        label: "Dispatch Centre",
        children: [
          {
            key: "dispatchSummary",
            icon: <BarChartOutlined />,
            label: "Dispatch Summary",
            path: "/dispatch/summary",
            requiredPermissions: ["dispatch:read"],
          },
          {
            key: "dispatchStrategy",
            icon: <ExperimentOutlined />,
            label: "Dispatch Strategy",
            path: "/dispatch/strategy",
            requiredPermissions: ["dispatch:read"],
          },
          {
            key: "dispatchCurrent",
            icon: <SyncOutlined />,
            label: "Current Dispatch",
            path: "/dispatch/current",
            requiredPermissions: ["dispatch:read"],
          },
          {
            key: "dispatchBooked",
            icon: <CalendarOutlined />,
            label: "Booked Dispatch",
            path: "/dispatch/booked",
            requiredPermissions: ["dispatch:read"],
          },
          {
            key: "dispatchReport",
            icon: <FileTextOutlined />,
            label: "Dispatch Report",
            path: "/dispatch/report",
            requiredPermissions: ["report:read"],
          },
        ],
      },
    ],
  },
  {
    key: "accountManagement",
    icon: <DollarOutlined />,
    label: "Accounts",
    children: [
      {
        key: "accountSummary",
        icon: <LineChartOutlined />,
        label: "Summary",
        path: "/account/summary",
        requiredPermissions: ["ledger:read"],
      },
      {
        key: "userManagement",
        icon: <UserOutlined />,
        label: "User Management",
        path: "/account/users",
        requiredPermissions: ["user:read"],
      },
      {
        key: "statement",
        icon: <FileTextOutlined />,
        label: "Statement",
        path: "/account/statement",
        requiredPermissions: ["ledger:read"],
      },
    ],
  },
  {
    key: "tickets",
    icon: <CustomerServiceOutlined />,
    label: "Tickets",
    children: [
      {
        key: "ticketsSummary",
        icon: <UnorderedListOutlined />,
        label: "Summary",
        path: "/tickets/summary",
        requiredPermissions: ["ticket:read"],
      },
      {
        key: "ticketsSearch",
        icon: <SearchOutlined />,
        label: "Search",
        path: "/tickets/search",
        requiredPermissions: ["ticket:read"],
      },
    ],
  },
  {
    key: "helpFeedback",
    icon: <QuestionCircleOutlined />,
    label: "Help & Feedback",
    children: [
      {
        key: "bugReports",
        icon: <BugOutlined />,
        label: "Bug Reports",
        path: "/bug-reports",
      },
      {
        key: "portalGuide",
        icon: <QuestionCircleOutlined />,
        label: "Portal Guide",
        path: "/portal-guide",
      },
      {
        key: "engineGuide",
        icon: <CloudServerOutlined />,
        label: "Engine Guide",
        path: "/engine-guide",
        requiredRoleTypes: ["SUPERADMIN"],
      },
    ],
  },
  {
    key: "company",
    icon: <BankOutlined />,
    label: "Company",
    children: [
      {
        key: "companyList",
        icon: <UnorderedListOutlined />,
        label: "Company List",
        path: "/company/list",
        requiredPermissions: ["company:read"],
      },
      {
        key: "companySnapshot",
        icon: <PieChartOutlined />,
        label: "Company Snapshot",
        path: "/company/snapshot",
        requiredPermissions: ["company:read"],
      },
    ],
  },
  {
    key: "settlements",
    icon: <WalletOutlined />,
    label: "Settlements",
    children: [
      {
        key: "settlementRuns",
        icon: <ClockCircleOutlined />,
        label: "Settlement Runs",
        path: "/settlements/runs",
        requiredPermissions: ["settlement:read"],
      },
      {
        key: "settlementRules",
        icon: <SettingOutlined />,
        label: "Rule Management",
        path: "/settlements/rules",
        requiredPermissions: ["settlement:run"],
      },
      {
        key: "specialJournals",
        icon: <FileTextOutlined />,
        label: "Special Journals",
        path: "/settlements/journals",
        requiredPermissions: ["settlement:read"],
      },
      {
        key: "outstandingReceivable",
        icon: <AuditOutlined />,
        label: "Outstanding Receivable",
        path: "/settlements/receivable",
        requiredPermissions: ["settlement:read"],
      },
      {
        key: "outstandingPayable",
        icon: <TransactionOutlined />,
        label: "Outstanding Payable",
        path: "/settlements/payable",
        requiredPermissions: ["settlement:read"],
      },
      {
        key: "historicalReceivable",
        icon: <HistoryOutlined />,
        label: "Historical Receivable",
        path: "/settlements/history-receivable",
        requiredPermissions: ["settlement:read"],
      },
      {
        key: "historicalPayments",
        icon: <HistoryOutlined />,
        label: "Historical Payments",
        path: "/settlements/history-payments",
        requiredPermissions: ["settlement:read"],
      },
    ],
  },
  {
    key: "api",
    icon: <ApiOutlined />,
    label: "API",
    path: "/api",
    requiredPermissions: ["api:read"],
  },
  {
    key: "roleManagement",
    icon: <SettingOutlined />,
    label: "Roles & Permissions",
    path: "/roles",
    requiredPermissions: ["role:read"],
    requiredRoleTypes: ["SUPERADMIN"],
  },
  {
    key: "configuration",
    icon: <ControlOutlined />,
    label: "Configuration",
    requiredRoleTypes: ["SUPERADMIN"],
    children: [
      {
        key: "equipment",
        icon: <AppstoreOutlined />,
        label: "Equipment",
        path: "/configuration/equipment",
      },
      {
        key: "vppPlans",
        icon: <FileTextOutlined />,
        label: "VPP Plans",
        path: "/configuration/vpp-plans",
      },
      {
        key: "onboardingConfig",
        icon: <SyncOutlined />,
        label: "Onboarding Config",
        path: "/configuration/onboarding-config",
      },
    ],
  },
  {
    key: "auditLog",
    icon: <AuditOutlined />,
    label: "Audit Log",
    path: "/audit-log",
    requiredRoleTypes: ["SUPERADMIN"],
  },
  {
    key: "coreServices",
    icon: <CloudServerOutlined />,
    label: "Core Services",
    requiredRoleTypes: ["SUPERADMIN"],
    children: [
      {
        key: "engineStatus",
        icon: <ControlOutlined />,
        label: "Engine Status",
        path: "/core-services/engines",
      },
      {
        key: "redisStatus",
        icon: <DatabaseOutlined />,
        label: "Redis Monitor",
        path: "/core-services/redis",
      },
      {
        key: "backtest",
        icon: <ExperimentOutlined />,
        label: "Backtest",
        path: "/core-services/backtest",
      },
      {
        key: "aemoSettlement",
        icon: <CloudServerOutlined />,
        label: "AEMO Settlement",
        path: "/core-services/aemo-settlement",
      },
    ],
  },
];

// Filter menu items based on user role and permissions
const filterMenuItems = (items, userRoleType, userPermissions) => {
  const isSuperadmin = userRoleType === "SUPERADMIN";

  return items
    .filter((item) => {
      // Superadmin sees everything
      if (isSuperadmin) return true;

      // Check role type restriction
      if (item.requiredRoleTypes && !item.requiredRoleTypes.includes(userRoleType)) {
        return false;
      }

      // Check permission restriction (user needs at least ONE of the listed permissions)
      if (item.requiredPermissions && item.requiredPermissions.length > 0) {
        if (!item.requiredPermissions.some((p) => userPermissions.includes(p))) {
          return false;
        }
      }

      return true;
    })
    .map((item) => {
      // Recursively filter children
      if (item.children) {
        return {
          ...item,
          children: filterMenuItems(item.children, userRoleType, userPermissions),
        };
      }
      return item;
    })
    // Remove parent items whose children are all filtered out
    .filter((item) => !item.children || item.children.length > 0);
};

// Get all keys and paths mapping
const getKeyPathMap = (items, map = {}) => {
  items.forEach((item) => {
    if (item.path) {
      map[item.key] = item.path;
    }
    if (item.children) {
      getKeyPathMap(item.children, map);
    }
  });
  return map;
};

// Get open keys from active key (supports nested submenus)
const getOpenKeys = (activeKey, items, parentKeys = []) => {
  for (const item of items) {
    if (item.key === activeKey) {
      return parentKeys;
    }
    if (item.children) {
      const result = getOpenKeys(activeKey, item.children, [...parentKeys, item.key]);
      if (result.length > 0) {
        return result;
      }
    }
  }
  return [];
};

// User dropdown items
const userMenuItems = [
  {
    key: "profile",
    icon: <UserOutlined />,
    label: "Profile",
  },
  {
    type: "divider",
  },
  {
    key: "logout",
    icon: <LogoutOutlined />,
    label: "Sign Out",
    danger: true,
  },
];

export default function PageLayout({ active, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();

  // Get user role type and permissions from localStorage (read fresh on every render)
  const userRoleType = localStorage.getItem("roleType") || "";
  const userPermissions = (() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  })();

  // Filter menu items based on user role and permissions
  const filteredMenuItems = useMemo(
    () => filterMenuItems(menuItems, userRoleType, userPermissions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userRoleType, JSON.stringify(userPermissions)]
  );

  // Create key-path mapping from filtered items
  const keyPathMap = useMemo(() => getKeyPathMap(filteredMenuItems), [filteredMenuItems]);

  // Get active key from current path or prop
  const activeKey = useMemo(() => {
    // First try to match by path
    for (const [key, path] of Object.entries(keyPathMap)) {
      if (location.pathname === path || location.pathname.startsWith(path + "/")) {
        return key;
      }
    }
    // Fallback to active prop
    return active || "dashboard";
  }, [location.pathname, keyPathMap, active]);

  // Get open keys for submenus (use filtered items)
  const defaultOpenKeys = useMemo(() => getOpenKeys(activeKey, filteredMenuItems), [activeKey, filteredMenuItems]);

  const handleMenuClick = ({ key }) => {
    const path = keyPathMap[key];
    if (path) {
      navigate(path);
    }
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === "logout") {
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("roleId");
      localStorage.removeItem("roleName");
      localStorage.removeItem("roleType");
      localStorage.removeItem("permissions");
      document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      navigate("/login");
    } else if (key === "profile") {
      navigate("/profile");
    } else if (key === "settings") {
      navigate("/settings");
    }
  };

  // Get user info from localStorage
  const userName = localStorage.getItem("username") || "User";
  const firstName = userName.split(" ")[0] || "User";
  const userInitials = userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  return (
    <Layout style={{ minHeight: "100vh", height: "100vh" }}>
      {/* Sidebar */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        collapsedWidth={80}
        style={{
          background: "linear-gradient(180deg, #2e3650 0%, #262B40 100%)",
          boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          overflow: "auto",
        }}
      >
        {/* Logo */}
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "0" : "0 24px",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <img
            src={collapsed ? LogoIcon : Logo}
            alt="Powow"
            style={{
              height: 32,
              width: "auto",
              transition: "all 0.2s",
            }}
          />
          {!collapsed && (
            <span
              style={{
                marginLeft: 12,
                color: "#fff",
                fontSize: 20,
                fontWeight: 600,
                whiteSpace: "nowrap",
                transform: "translateY(3px)",
              }}
            >
              VPP Platform
            </span>
          )}
        </div>

        {/* Navigation Menu */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[activeKey]}
          defaultOpenKeys={defaultOpenKeys}
          items={filteredMenuItems}
          onClick={handleMenuClick}
          style={{
            background: "transparent",
            borderRight: "none",
            marginTop: 8,
          }}
        />
      </Sider>

      {/* Main Layout */}
      <Layout
        style={{
          marginLeft: collapsed ? 80 : 260,
          transition: "margin-left 0.2s",
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Header
          style={{
            height: 64,
            lineHeight: "64px",
            padding: "0 24px",
            background: token.colorBgContainer,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            flexShrink: 0,
            zIndex: 99,
          }}
        >
          {/* Collapse Button */}
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: 18,
              width: 48,
              height: 48,
            }}
          />

          {/* User Dropdown */}
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleUserMenuClick,
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
                padding: "8px 12px",
                borderRadius: 8,
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <Avatar
                style={{
                  background: "linear-gradient(135deg, #54A2CC 0%, #3d8ab8 100%)",
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </Avatar>
              <span style={{ color: token.colorText, fontWeight: 500 }}>
                {firstName}
              </span>
            </div>
          </Dropdown>
        </Header>

        {/* Content */}
        <Content
          style={{
            padding: 24,
            overflow: "auto",
            flex: 1,
            background: token.colorBgLayout,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
